import { defineComponent, getCurrentInstance, h, type SetupContext, type VNode } from 'vue';
import { createVuetify, type ThemeDefinition } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
// Vuetify 3's core stepper has no vertical mode; the labs one is what the two
// v2 `<v-stepper vertical>` screens (pipeline stages, add occupier) use.
import { VStepperVertical, VStepperVerticalItem } from 'vuetify/labs/VStepperVertical';
import { aliases, mdi } from 'vuetify/iconsets/mdi';

import { readThemePreference } from '@/bundles/App/helpers/themePreference';

// Vuetify 3 ships a different light palette (primary #1867C0, secondary
// #48A9A6, error #B00020). Every `color="primary"` in this app was chosen
// against Vuetify 2's palette, so keep those values.
const light: ThemeDefinition = {
  colors: {
    primary: '#1976D2',
    secondary: '#424242',
    accent: '#82B1FF',
    error: '#FF5252',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FB8C00',
    // v2 resolved `color="rust"` to the app's `.rust` class (styles.scss);
    // v3 needs it in the theme or `bg-rust` paints nothing.
    rust: '#871E1B',
    // Availabilities pages (`sc-avail-*`), each light value the literal it
    // replaced:
    // - surface-tint (not avail-only): the Sale / Lease Details blocks of the
    //   availability create and edit forms (v2 grey lighten-5) and the tinted
    //   property column of the spec-card comparison forms (.details-title)
    // - step-pending: a workflow step not done yet, in the list's Workflow
    //   view (grey lighten-2) and on the record's Workflow card (-strong,
    //   grey lighten-1); dimmer than the green/amber/red of a done step in
    //   both themes
    'sc-surface-tint': '#FAFAFA',
    'sc-avail-step-pending': '#E0E0E0',
    'sc-avail-step-pending-strong': '#BDBDBD',
    // App surface and text tokens (`sc-*`). The app sheets and templates
    // hard-coded these light greys, which stayed light in dark mode; they read
    // them from the theme now, so each light value here is the literal it
    // replaced and must not move. Every token needs a dark twin below
    // (src/__tests__/themeTokens.spec.ts).
    // - raised: a control sitting on a surface (search field, toggle group,
    //   pagination buttons)
    // - surface-soft: v2's #f5f5f5 grounds (contained and toggle buttons,
    //   hovered filter rows)
    // - chip: v2's colourless chip
    // - subtle: zebra rows and tinted blocks inside a card
    // - muted: docked action bars and footers of side panels and drawers
    // - subbar: the list page's filter / sort bar under the title bar
    // - header: data-table header rows, footers and pagination bars
    // - row-hover: a hovered list-table row (light keeps the surface-light it
    //   used; dark sits between the zebra row and the header, so a hovered row
    //   never reads as a header)
    // - divider: the solid hairline above a docked action bar
    // - frame: the list pages' table/map frame and its drag handle
    // - hairline: the faint rule between saved-set menu rows
    // - selected / selected-text: the highlighted (default) row of a menu
    // - text-strong: record-card titles and hovered sort headers (#333)
    // - text-muted: secondary copy the app set in #555
    // - text-subtle / text-hint: the filter bar's "Sort:" / "Filters:" labels
    //   and its "Click here to filter your results" link
    // - text-faint: de-emphasised ids and placeholders set in #ccc
    // - checkbox: a list row's select checkbox (#363636 rather than primary)
    // - surface-overlay: menus and select dropdowns, one step above the cards
    //   they open over (Vuetify paints them in plain `surface`)
    // - menu-muted: the list Actions menu, grey in light (v2's grey-lighten-4
    //   list, black text) and an ordinary menu in dark
    // - editor-ink / editor-line: Quill's toolbar icons and picker text (#444)
    //   and its picker/tooltip rules (#ccc)
    // - card-shadow: the colour of the app's soft card shadow (#555)
    'sc-surface-raised': '#FFFFFF',
    'sc-surface-soft': '#F5F5F5',
    'sc-chip': '#E0E0E0',
    'sc-surface-subtle': '#F9F9F9',
    'sc-surface-muted': '#F7F7F7',
    'sc-subbar': '#EFEFEF',
    'sc-surface-header': '#E0E0E0',
    'sc-row-hover': '#EEEEEE',
    'sc-divider': '#DBDBDB',
    'sc-frame': '#CBD5E0',
    'sc-hairline': '#F3F3F3',
    'sc-selected': '#E4EDF8',
    'sc-selected-text': '#1867C0',
    'sc-text-strong': '#333333',
    'sc-text-muted': '#555555',
    'sc-text-subtle': '#666666',
    'sc-text-hint': '#8C8584',
    'sc-text-faint': '#CCCCCC',
    'sc-checkbox': '#363636',
    'sc-surface-overlay': '#FFFFFF',
    'sc-menu-muted': '#F5F5F5',
    'on-sc-menu-muted': '#000000',
    'sc-editor-ink': '#444444',
    'sc-editor-line': '#CCCCCC',
    'sc-card-shadow': '#555555',
    'sc-upload-surface': '#EEEEEE',
    // Dashboards (`sc-dash-*`): the five dashboards and their charts were
    // drawn in their own slate greys, so each light value is the literal it
    // replaced (1-5 units apart ones share a token).
    // - text / text-ink: figures and titles (#1f2328), the comparables
    //   figures and selected pill (#11151a)
    // - text-muted / -soft / -subtle: labels and captions, darkest first
    // - year-text: the year cards' figures
    // - border / border-strong: card and row outlines, bar tracks
    // - surface-subtle: kanban columns and tinted rows inside a card
    // - skeleton: loading-bar sweep and empty bar tracks, and the tile behind
    //   a company logo or its initials (LogoAvatar)
    // - logo-text: those initials on the kanban cards
    // - year-muted: the year cards' captions and hints
    // - selected: the selected kanban card
    // - paid / due: payment-status figures and the paid/booked bars
    // - chart-label / chart-axis / chart-grid: ECharts data labels, axis
    //   lines and labels, split lines (the axis and grid values are ECharts'
    //   own light defaults)
    'sc-dash-text': '#1F2328',
    'sc-dash-text-ink': '#11151A',
    'sc-dash-text-muted': '#66707B',
    'sc-dash-text-soft': '#6F7782',
    'sc-dash-text-subtle': '#7B838E',
    'sc-dash-year-text': '#27272A',
    'sc-dash-border': '#E3E6EA',
    'sc-dash-border-strong': '#D8DDE3',
    'sc-dash-surface-subtle': '#F7F9FB',
    'sc-dash-skeleton': '#EEF1F4',
    'sc-dash-logo-text': '#4B5563',
    'sc-dash-year-muted': '#9CA3AF',
    'sc-dash-selected': '#EFF6FF',
    'sc-dash-paid': '#1B5E20',
    'sc-dash-due': '#B7791F',
    'sc-dash-chart-label': '#3F3F46',
    'sc-dash-chart-axis': '#6E7079',
    'sc-dash-chart-grid': '#E0E6F1',
    // Map (`sc-map-*`): mapbox's own controls, which its CDN stylesheets paint
    // in fixed light greys; each light value is the literal it replaced.
    // - control-border: the rule between stacked zoom / compass buttons
    // - text / hover: the search box's suggestion rows and the hovered one
    'sc-map-control-border': '#DDDDDD',
    'sc-map-text': '#404040',
    'sc-map-hover': '#F3F3F3',
    // - popup-rule: the rule under a map popup's title
    'sc-map-popup-rule': '#D9D9D9',
    // - style-custom: the icon of an account's own (custom) base map style
    'sc-map-style-custom': '#98002E',
    // Status and utility colours that were CSS literals, each light value the
    // literal it replaced:
    // - toolbar-muted: the grey title bar of the Change Log dialog
    // - index-chip: a record-number chip with no property-type colour
    // - text-archive: the red "Archive" text button
    // - text-overdue / text-paid: an invoice's status word
    'sc-toolbar-muted': '#9E9E9E',
    'sc-index-chip': '#CCCCCC',
    'sc-text-archive': '#F44336',
    'sc-text-overdue': '#FF0000',
    'sc-text-paid': '#008000',
  },
  variables: {
    // A disabled button's label and icon (v2's 26% black), and the extra
    // ground a disabled button in a toggle group gets (v2's 12% black).
    'sc-disabled-ink-opacity': 0.26,
    'sc-toggle-disabled-ground-opacity': 0.12,
    // mapbox's half-white attribution strip
    'sc-map-attrib-opacity': 0.5,
    // what an unselected dashboard year card adds to its dimmed opacity
    'sc-dash-year-dimmed-lift': 0,
    // text the app sets in the full on-surface ink (pure black here)
    'sc-ink-opacity': 1,
  },
};

// The app-wide dark mode (profile menu > Theme), and through `darkChrome` below
// the palette of the always-dark chrome. Vuetify merges it over its built-in
// dark theme, whose `surface-bright` is a lavender (#ccbfd6): the one rule in
// Vuetify's stylesheet that reads it paints the switch thumb, so every switch
// in the map drawers had a pink thumb where Vuetify 2 drew grey 400. Material's dark
// surfaces: a #121212 page, cards one step lighter so elevation reads as
// lightness rather than shadow, and status colours desaturated so they do not
// glare on grey. Primary stays the light theme's #1976D2 so the brand blue of
// filled buttons, title bars and selected states is the same in both themes;
// Vuetify derives a white on-primary for it, as in light.
// surface-variant is the dark sheet the light theme also uses (Vuetify's dark
// default is a light grey) because snackbars are drawn on it and their
// content -- the map's radius slider, its white coordinates -- expects a dark
// ground.
const dark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#121212',
    surface: '#1e1e1e',
    'surface-light': '#2c2c2c',
    'surface-bright': '#bdbdbd',
    'surface-variant': '#424242',
    'on-surface-variant': '#EEEEEE',
    primary: '#1976D2',
    secondary: '#9e9e9e',
    accent: '#82B1FF',
    // Red 400 lifted so red text reaches 4.5:1 on every dark ground it sits on
    // (#EF5350 was 4.3:1 on the #272727 panel footers); filled error buttons
    // and chips take black text on it (7:1, white was 3:1).
    error: '#F26B69',
    'on-error': '#000000',
    info: '#42A5F5',
    success: '#66BB6A',
    warning: '#FFA726',
    // Filled warning buttons (Confirm Archive) keep the light theme's white
    // text instead of the black Vuetify picks for this lighter orange.
    'on-warning': '#FFFFFF',
    rust: '#B5473F',
    // Tinted blocks (deal terms, the property comparison column) a step above
    // the side panel's surface; a pending step stays the dimmest mark in its row (3:1 on a
    // card) instead of turning into the brightest one.
    'sc-surface-tint': '#262626',
    'sc-avail-step-pending': '#6B6B6B',
    'sc-avail-step-pending-strong': '#6B6B6B',
    // Dark twins of the `sc-*` tokens above: each surface a step lighter than
    // the one it sits on (page #121212 < card #1e1e1e < rows < bars < headers),
    // text at AA or better on a card (#bdbdbd 8.9:1, #969696 5.6:1 and 4.7:1
    // on a hovered row). Menus sit on #2c2c2c, a step above the cards.
    'sc-surface-raised': '#353535',
    'sc-surface-soft': '#3A3A3A',
    'sc-chip': '#4A4A4A',
    'sc-surface-subtle': '#242424',
    'sc-surface-muted': '#272727',
    'sc-subbar': '#272727',
    'sc-surface-header': '#2C2C2C',
    'sc-row-hover': '#282828',
    'sc-divider': '#3D3D3D',
    'sc-frame': '#424242',
    'sc-hairline': '#383838',
    'sc-selected': '#233447',
    'sc-selected-text': '#90CAF9',
    'sc-text-strong': '#E2E2E2',
    'sc-text-muted': '#BDBDBD',
    'sc-text-subtle': '#B0B0B0',
    'sc-text-hint': '#ADA6A5',
    'sc-text-faint': '#969696',
    'sc-checkbox': '#E0E0E0',
    'sc-surface-overlay': '#2C2C2C',
    // 87% white on the menu ground, like the other dark menus' text.
    'sc-menu-muted': '#2C2C2C',
    'on-sc-menu-muted': '#DEDEDE',
    'sc-editor-ink': '#A8A8A8',
    'sc-editor-line': '#4A4A4A',
    // The page ground: dark surfaces show depth by lightness, so the card
    // shadow blends away instead of drawing a grey or black band under cards.
    'sc-card-shadow': '#121212',
    // The upload modal sits on the dialog ground, like the other dialogs.
    'sc-upload-surface': '#1E1E1E',
    // Dashboards: text at AA or better on a card (#98a0aa 6.3:1), the paid
    // green and due amber lifted off the dark green / brown that fell to 2:1
    // and 4:1 on #1e1e1e, hairlines at roughly 12% white.
    'sc-dash-text': '#E6E8EB',
    'sc-dash-text-ink': '#E6E8EB',
    'sc-dash-text-muted': '#A3ABB5',
    'sc-dash-text-soft': '#9EA6B0',
    'sc-dash-text-subtle': '#98A0AA',
    'sc-dash-year-text': '#E4E4E7',
    'sc-dash-border': '#383B40',
    'sc-dash-border-strong': '#4A4E54',
    'sc-dash-surface-subtle': '#252729',
    'sc-dash-skeleton': '#2E3033',
    'sc-dash-logo-text': '#B8BEC7',
    'sc-dash-year-muted': '#A9AFB9',
    'sc-dash-selected': '#1C2B3D',
    'sc-dash-paid': '#66BB6A',
    'sc-dash-due': '#D69E2E',
    'sc-dash-chart-label': '#D4D4D8',
    'sc-dash-chart-axis': '#A1A3AB',
    'sc-dash-chart-grid': '#363A40',
    // Map: controls sit on the card surface, so the hovered row is one step
    // lighter and the button rule roughly 18% white.
    'sc-map-control-border': '#424242',
    'sc-map-text': '#E0E0E0',
    'sc-map-hover': '#2C2C2C',
    'sc-map-popup-rule': '#3D3D3D',
    // The custom style's crimson mixed 40% toward white, the first step
    // `getReadableMarkColor` finds at 3:1 on a hovered drawer row (#2c2c2c).
    'sc-map-style-custom': '#C16682',
    // White text on the title bar at 10:1 instead of 2.7:1 on grey; the index
    // chip one step above the chip grey so its white number reads (6.7:1); red
    // and green text lifted to AA on a card (the archive red is also used on
    // the #272727 panel footers).
    'sc-toolbar-muted': '#424242',
    'sc-index-chip': '#5C5C5C',
    'sc-text-archive': '#E57373',
    'sc-text-overdue': '#EF5350',
    'sc-text-paid': '#66BB6A',
    // Text on a table footer bar (`bg-sc-surface-header`): Vuetify derives a
    // white on-colour, this is Material's 87% white over the bar.
    'on-sc-surface-header': '#E4E4E4',
  },
  variables: {
    // Material's 38% for disabled text on dark (26% white was 2:1), and no
    // extra ground: 12% white made a disabled toggle button the lightest of
    // its group, so it looked selected rather than off.
    'sc-disabled-ink-opacity': 0.38,
    'sc-toggle-disabled-ground-opacity': 0,
    // 60% of #1e1e1e over the light basemap: a lighter strip than the old
    // near-solid 85%, readable because its links turn light in the dark
    // theme (_mapbox.scss) instead of primary, which fell to 1.5:1 there.
    'sc-map-attrib-opacity': 0.6,
    // Opacity flattens dark contrast faster than light: at 0.55 the cards'
    // captions fell to 2.9:1, so unselected cards fade less.
    'sc-dash-year-dimmed-lift': 0.2,
    // Material's dark text emphasis, the light theme's 87 / 60 / 38%. Vuetify's
    // dark default is 100 / 70 / 50%, which set every heading and body line in
    // pure white and glared on the grey cards (.87 white is still 13:1 on a
    // card, .60 is 6.7:1).
    'high-emphasis-opacity': 0.87,
    'medium-emphasis-opacity': 0.6,
    'disabled-opacity': 0.38,
    // The full-ink text of the light theme (black names and links) at the same
    // 87% rather than pure white.
    'sc-ink-opacity': 0.87,
  },
};

// The sidebar, the map's layers and tools drawers, the map's radius snackbar
// and the two coloured toolbars that ask for `theme="darkChrome"` are dark in
// both modes. They were drawn against Vuetify's dark text emphasis (white text,
// 70% secondary, 50% disabled) and keep it, with the near-white text tokens
// `dark` had before it took Material's emphasis, so the app-wide dark mode's
// dimmer text does not reach them and light mode does not move. A sheet rule
// written for `.v-theme--dark` must also name `.v-theme--darkChrome`.
const darkChrome: ThemeDefinition = {
  ...dark,
  colors: {
    ...dark.colors,
    'sc-text-strong': '#EEEEEE',
    'sc-dash-text-ink': '#F4F5F7',
    'on-sc-surface-header': '#FFFFFF',
  },
  variables: {
    ...dark.variables,
    'high-emphasis-opacity': 1,
    'medium-emphasis-opacity': 0.7,
    'disabled-opacity': 0.5,
    'sc-ink-opacity': 1,
  },
};

type SetupFn = (props: Record<string, unknown>, ctx: SetupContext) => unknown;
type PropReader = (key: string | symbol, value: unknown, props: Record<string, unknown>, ctx: SetupContext, vnode: VNode) => unknown;

// Keeps Vuetify's own component and setup -- same instance, props, emits, slots
// and exposed refs (`validate()`, `menu`, `blur()` ...) -- and only changes what
// its setup reads for some props. The reader sits outside Vuetify's defaults
// layer, so a prop it decides must not also have a `defaults` entry below:
// Vuetify would read that entry instead of what the reader returned.
const withProps = <T extends { setup?: unknown }>(component: T, readProp: PropReader): T => ({
  ...component,
  setup (props: Record<string, unknown>, ctx: SetupContext) {
    const vm = getCurrentInstance();
    const readProps = new Proxy(props, {
      get: (target, key) => readProp(key, Reflect.get(target, key), target, ctx, vm!.vnode),
    });

    return (component.setup as SetupFn)(readProps, ctx);
  },
});

// Vuetify 2's select and autocomplete built their selection only from values
// found in `items`, so the `''` a form model starts with (never an item) meant
// "nothing selected": the field was not dirty, its label stayed in place and a
// `single-line` label stayed visible. Vuetify 3 turns any non-null modelValue
// into a selection, so `''` became a selection with an empty title that marks the
// field dirty: the Attachment form's single-line "File Type" had no label at
// all, and the Pipeline form's Property Type / Transaction Category labels
// floated over nothing. A `''` model is read as `null`, which v3 treats as no
// selection; `update:modelValue` is still emitted with the chosen value.
// A single VCombobox gets the same reading: `''` is no typed text either, but v3
// still counted it as a selection, so the Add Location form's City and County
// labels floated over empty fields. Only the initial read changes; whatever the
// user types or picks is emitted as before.
const readEmptyModelUnselected: PropReader = (key, value) => (key === 'modelValue' && value === '' ? null : value);

// Vuetify 2's autocomplete and combobox always drew their dropdown arrow, but
// showed the menu only when the search left items to show or a `no-data` slot
// was given (VAutocomplete `menuCanShow`); a bare "No data available" list
// never appeared. Vuetify 3 has one switch for both: `hideNoData` off shows that
// list on an empty field (the Street Address combobox before a search), on
// drops VCombobox's arrow while it has no items and refuses to open the menu,
// so a menu opened before async items arrive (FilterUseTypeMultipleSelect)
// stayed shut. So unless a call site sets `hide-no-data` itself:
// - a `no-data` slot keeps the no-data menu, arrow and hint, as in v2;
// - with items, `hideNoData` is on: the arrow stays and the menu shows only
//   while the search leaves some of them;
// - with no items, `hideNoData` stays off (arrow drawn, menu may be opened) and
//   the VMenu is held shut until items arrive, as v2 did
//   (`value: menuCanShow && isMenuActive`).
// VSelect keeps its no-data menu: v2's plain select showed it.
const readNoDataLikeVue2: PropReader = (key, value, props, ctx, vnode) => {
  if (key !== 'hideNoData' && key !== 'menuProps') return value;

  const isSetByCaller = vnode.props?.hideNoData !== undefined || vnode.props?.['hide-no-data'] !== undefined;
  const hasItems = Boolean((props.items as unknown[] | undefined)?.length);

  if (isSetByCaller) return value;
  if (key === 'hideNoData') return !ctx.slots['no-data'] && hasItems;

  return hasItems || ctx.slots['no-data'] ? value : { ...(value as Record<string, unknown> | undefined), modelValue: false };
};

// Vuetify 2's `dense` select, autocomplete and combobox drew a `dense` list in
// their menu (40px rows, 13px/500 text). Vuetify 3 does not hand the field's
// density to its menu VList, so the "Add to Set" autocomplete's options grew
// from 50px to 62px with 16px titles. A call site that sets `density="compact"`
// (the migrated `dense`) gets a compact list, unless it set `listProps.density`
// itself. Only the call site's density counts, not the `compact` default below:
// v2's non-dense selects kept a normal list.
const readDenseListLikeVue2: PropReader = (key, value, props, ctx, vnode) => {
  if (key !== 'listProps' || vnode.props?.density !== 'compact') return value;

  const listProps = value as Record<string, unknown> | undefined;
  if (listProps?.density !== undefined) return value;

  return { ...listProps, density: 'compact' };
};

export default createVuetify({
  components: {
    ...components,
    VSelect: withProps(components.VSelect, (key, value, props, ctx, vnode) => (
      readDenseListLikeVue2(key, readEmptyModelUnselected(key, value, props, ctx, vnode), props, ctx, vnode)
    )),
    VAutocomplete: withProps(components.VAutocomplete, (key, value, props, ctx, vnode) => (
      readDenseListLikeVue2(key, readNoDataLikeVue2(key, readEmptyModelUnselected(key, value, props, ctx, vnode), props, ctx, vnode), props, ctx, vnode)
    )),
    VCombobox: withProps(components.VCombobox, (key, value, props, ctx, vnode) => {
      const model = props.multiple ? value : readEmptyModelUnselected(key, value, props, ctx, vnode);

      return readDenseListLikeVue2(key, readNoDataLikeVue2(key, model, props, ctx, vnode), props, ctx, vnode);
    }),
    VStepperVertical,
    VStepperVerticalItem,
  },
  directives,
  display: {
    // Vuetify 2 treated anything under 1264px as mobile; keep that exact
    // threshold so `$vuetify.display.mobile` branches behave as they did.
    mobileBreakpoint: 1264,
  },
  defaults: {
    // Vuetify 3's VCardActions forces `variant: 'text'` + `slim` on the buttons
    // inside it, so every dialog/drawer footer lost its filled primary button.
    // Vuetify 2 left those buttons at the default contained style.
    VCardActions: {
      // Vuetify 2 left card-action buttons at the contained default; v3's
      // VCardActions forces variant="text" on them. `slim` stays: v2 padded
      // these buttons `0 8px` (`.v-card__actions > .v-btn.v-btn`), which is
      // exactly what v3's slim does. Measured on the Create Task panel: Close
      // 64px wide on 8080, 74px on 8091 without slim.
      VBtn: { variant: 'elevated', slim: true },
    },
    // Vuetify 2's v-btn-toggle `color` tinted the ACTIVE button's text
    // (`primary--text`) over the plain button ground; v3 applies it as the
    // active button's fill (`bg-primary`, white text). With the text variant
    // v3 emits `text-primary` instead, and the parity sheet restores the v2
    // ground on the buttons.
    VBtnToggle: { variant: 'text' },
    // Menus keep Vuetify 3's own transition (scale out of the activator); the
    // Vuetify 2 fade is not copied on purpose.
    // Vuetify 2's menu never moved focus into its content when it opened.
    // Vuetify 3's `captureFocus` pulls the next focus change into the menu's
    // first focusable child, and the browser paints that programmatic focus
    // as `:focus-visible` -- the profile menu's close button showed a grey
    // disc and ring that 8080 never drew.
    VMenu: { captureFocus: false },
    // Vuetify 2 built a tooltip's content the first time it was shown. Vuetify
    // 3's VTooltip is `eager` by default, so every tooltip on a page renders
    // its content into the overlay container up front: the availabilities list
    // carried 169 hidden overlays for its 50 rows' chips before anything was
    // hovered. Lazy is v2's cost; the content still renders on first hover.
    VTooltip: { eager: false },
    // Same story for toolbars and banner actions, which also hand every button
    // inside them variant="text". An icon button is painted transparent again by
    // the parity sheet, as it was in Vuetify 2.
    VToolbar: {
      VBtn: { variant: 'elevated' },
    },
    VBannerActions: {
      VBtn: { variant: 'elevated', slim: false },
    },
    // Vuetify 2's selection controls were primary when checked; v3 leaves them
    // at the default on-surface colour unless a `color` is given.
    VCheckbox: { color: 'primary', hideDetails: 'auto', density: 'compact' },
    // ...and v2's `v-simple-checkbox` defaulted its own colour to primary.
    VCheckboxBtn: { color: 'primary', density: 'compact' },
    // Vuetify 2's date picker filled the picked day with primary. Vuetify 3 draws it as a flat button in the
    // picker's own `color`, and with none given that is the calendar's surface, so the picked day did not show
    // in either theme (today's outline still did).
    VDatePicker: { color: 'primary' },
    // Vuetify 2's image filled its box and `contain` opted out; Vuetify 3
    // inverted that pair, so every image the conversion left bare -- 94 of the
    // app's 139 -- letterboxed itself inside its box. The profile menu's avatar
    // was a band across a grey circle. The call sites that did ask for `contain`
    // now say `:cover="false"`. Not `eager`: Vuetify 2 lazy-loaded too (2 of
    // 50 list covers loaded on the properties list), and the covers are
    // CSS-sized, so nothing reflows.
    VImg: { cover: true },
    // `inset` is the app's house style rather than a Vuetify 2 carry-over: 81 of
    // the 150 switches already asked for it one by one, and the rest -- the map
    // drawers among them -- are meant to look the same. A switch that needs the
    // thin v2 track can still pass `:inset="false"`.
    VSwitch: { color: 'primary', hideDetails: 'auto', density: 'compact', inset: true },
    // A radio group hands its own density to the radios inside it.
    VRadioGroup: { color: 'primary', hideDetails: 'auto', density: 'compact' },
    VRadio: { color: 'primary' },
    // Vuetify 2's item-text defaulted to `text`; v3's item-title defaults to
    // `title`, so every select whose items are the app's `{ text, value }` shape
    // and that does not pass item-title renders [object Object].
    // Every text field, textarea, file input and select is outlined unless the
    // call site asks for another variant. Vuetify 3's own default is `filled`
    // (a 4% grey ground); Vuetify 2's was the bare underlined field, which the
    // app kept at first and has since dropped for the outlined look. A call
    // site's own `variant` still wins over these defaults.
    // Every input also drops its details row until it has something to say
    // (`hide-details="auto"`), so a form spends those 22px only on a field
    // carrying a hint, a counter or an error, and runs at the compact density
    // (`density="compact"`), which trims 8px off each control. A call site's
    // own `hide-details` or `density` still wins.
    VTextField: { variant: 'outlined', hideDetails: 'auto', density: 'compact' },
    VTextarea: { variant: 'outlined', hideDetails: 'auto', density: 'compact' },
    VFileInput: { variant: 'outlined', hideDetails: 'auto', density: 'compact' },
    // `hideNoData: false` is Vuetify 2's default for the plain select. The
    // autocomplete and combobox get theirs from `readNoDataLikeVue2` above, so
    // they must not carry a `hideNoData` default here.
    VSelect: { itemTitle: 'text', variant: 'outlined', hideDetails: 'auto', density: 'compact', hideNoData: false },
    VAutocomplete: { itemTitle: 'text', variant: 'outlined', hideDetails: 'auto', density: 'compact' },
    VCombobox: { itemTitle: 'text', variant: 'outlined', hideDetails: 'auto', density: 'compact' },
    VSlider: { hideDetails: 'auto', density: 'compact' },
    // ...but Vuetify's own data-table footer feeds its per-page select
    // `{ title, value }` items, so the `text` default above rendered
    // "[object Object]" there (dashboard Open Balances / Invoices footers).
    VDataTableFooter: {
      VSelect: { itemTitle: 'title' },
    },
    // ...and so does the sort select the headers render in mobile mode
    // (`{ title, key }` items).
    VDataTableHeaders: {
      VSelect: { itemTitle: 'title' },
    },
    // v2's data-table footer read "Per page:"; v3 defaults to "Items per page:".
    // One up arrow for both sort directions, flipped for descending in the
    // parity sheet (as BaseTable's header arrow turns), instead of v3's
    // up / down glyph swap.
    VDataTable: { itemsPerPageText: 'Per page:', sortAscIcon: 'mdi-arrow-up', sortDescIcon: 'mdi-arrow-up' },
    VDataTableServer: { itemsPerPageText: 'Per page:', sortAscIcon: 'mdi-arrow-up', sortDescIcon: 'mdi-arrow-up' },
    // Vuetify 2's linear loader was primary unless told otherwise (`color`
    // defaulted to 'primary'); v3 has no default and paints currentColor, so
    // every field, table and card loader went black.
    VProgressLinear: { color: 'primary' },
    // Vuetify 2 chips were filled; v3 defaults to the `tonal` variant, so a
    // `color="black"` chip renders as a faint tint instead of a black pill.
    // Every chip in this app is a small label chip (Vuetify 2's
    // `<v-chip label small>`), which is what CommonChip has been supplying
    // one call site at a time; a bare v-chip now gets the same look.
    VChip: { variant: 'flat', label: true, size: 'small' },
    // Vuetify 2 drew a divider as rgba(0,0,0,.12). Vuetify 3 draws it in the
    // surrounding text colour (87% black on a surface) at 12% opacity, i.e.
    // ~10%; 0.138 x 0.87 gives the v2 12% while `color` / `opacity` on a
    // divider still behave exactly as documented.
    VDivider: { opacity: 0.138 },
  },
  theme: {
    // The saved System / Light / Dark choice (profile menu), read before the
    // first render so a dark page never flashes light.
    defaultTheme: readThemePreference(),
    themes: { light, dark, darkChrome },
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi },
  },
});
